import { useState, useEffect, useCallback } from 'react';
import { useDataEngine } from '@dhis2/app-runtime';

const RATING_SCALE_NAMESPACE = 'performance-rating-scale';

const getDefaultRatingScale = () => ({
  '5': {
    label: 'Outstanding',
    description: 'Consistently exceeds expectations and demonstrates exceptional performance. Sets new standards and serves as a role model.',
    shortDesc: 'Exceptional performance',
    color: '#28a745'
  },
  '4': {
    label: 'Exceeds Expectations',
    description: 'Often exceeds targets and requirements. Shows initiative and consistently delivers high-quality results.',
    shortDesc: 'Above standard',
    color: '#24f05a'
  },
  '3': {
    label: 'Meets Expectations',
    description: 'Consistently meets all job requirements and objectives. Demonstrates solid, reliable performance.',
    shortDesc: 'Meets standard',
    color: '#ffc107'
  },
  '2': {
    label: 'Needs Improvement',
    description: 'Sometimes falls short of requirements. Requires guidance and development in certain areas.',
    shortDesc: 'Below standard',
    color: '#fd7e14'
  },
  '1': {
    label: 'Unsatisfactory',
    description: 'Does not meet minimum requirements. Significant improvement needed or performance improvement plan required.',
    shortDesc: 'Fails to meet standard',
    color: '#dc3545'
  },
  '0': {
    label: 'Not Rated',
    description: 'No rating provided yet.',
    shortDesc: 'Not evaluated',
    color: '#6c757d'
  }
});

const getDataStoreKey = (orgId) => (orgId ? `scale_${orgId}` : 'scale_global');

/**
 * Build the FULL ancestor chain for an org unit, from the unit itself up
 * to (but not including) the DHIS2 root. Uses `path` so we don't make
 * one API call per level.
 *
 * Example path: "/rootId/lvl2Id/lvl3Id/lvl4Id/lvl5Id"
 * orgUnitId     : "lvl5Id"
 * → returns    : ["lvl5Id", "lvl4Id", "lvl3Id", "lvl2Id", "rootId"]
 */
const buildAncestorChain = async (engine, orgUnitId) => {
  if (!orgUnitId) return [];
  try {
    const response = await engine.query({
      orgUnit: {
        resource: `organisationUnits/${orgUnitId}`,
        params: { fields: 'id,path,parent[id]' }
      }
    });
    const path = response.orgUnit?.path;
    if (path) {
      // split removes empty leading slash, keep order, then reverse so
      // we try the closest ancestor first
      const parts = path.split('/').filter(Boolean);
      return [...parts].reverse();
    }
    // No path → at least try the unit and its immediate parent
    const chain = [orgUnitId];
    if (response.orgUnit?.parent?.id) chain.push(response.orgUnit.parent.id);
    return chain;
  } catch (err) {
    console.error('Failed to build ancestor chain:', err);
    return [orgUnitId];
  }
};

export const useRatingScale = (orgUnitId, scaleType = 'business') => {
  const engine = useDataEngine();
  const [ratingScale, setRatingScale] = useState(getDefaultRatingScale());
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const RATING_SCALE_NAMESPACE = scaleType === 'leadership'
    ? 'leadership-rating-scale'      
    : 'performance-rating-scale';  

  const loadRatingScale = useCallback(async () => {
    if (!orgUnitId) {
      setRatingScale(getDefaultRatingScale());
      setLoading(false);
      return;
    }

    try {
      // 1. Full chain from subject up to root
      const chain = await buildAncestorChain(engine, orgUnitId);

      // 2. Try each ancestor's scale_<id> in order (closest first)
      let loadedScale = null;
      let loadedFrom = null;
      for (const ancestorId of chain) {
        const key = getDataStoreKey(ancestorId);
        try {
          const res = await engine.query({
            ratingScale: {
              resource: `dataStore/${RATING_SCALE_NAMESPACE}/${key}`
            }
          });
          const data = res.ratingScale;
          if (data && typeof data === 'object' && Object.keys(data).length > 0) {
            loadedScale = data;
            loadedFrom = ancestorId;
            break;
          }
        } catch (err) {
          // 404 → try next ancestor
        }
      }

      // 3. Fall back to global
      if (!loadedScale) {
        try {
          const globalRes = await engine.query({
            ratingScale: {
              resource: `dataStore/${RATING_SCALE_NAMESPACE}/scale_global`
            }
          });
          const data = globalRes.ratingScale;
          if (data && typeof data === 'object' && Object.keys(data).length > 0) {
            loadedScale = data;
            loadedFrom = 'global';
          }
        } catch (err) {
          // No global scale → defaults
        }
      }

      // 4. Apply
      if (loadedScale) {
        console.log(`🎨 Rating scale loaded from ${loadedFrom}`);
        setRatingScale(loadedScale);
      } else {
        console.log('🎨 Rating scale: using hardcoded defaults');
        setRatingScale(getDefaultRatingScale());
      }
      setError(null);
    } catch (err) {
      console.error('Failed to load rating scale:', err);
      setError(err);
      setRatingScale(getDefaultRatingScale());
    } finally {
      setLoading(false);
    }
  }, [engine, orgUnitId, RATING_SCALE_NAMESPACE]);

  // Initial load + reload when orgUnitId changes
  useEffect(() => {
    setLoading(true);
    loadRatingScale();
  }, [loadRatingScale]);

  // 🔄 Live refresh: listen for saves from RatingScaleAdmin
  useEffect(() => {
    const handler = () => {
      console.log('🔄 rating-scale-updated event received, refetching…');
      loadRatingScale();
    };
    window.addEventListener('rating-scale-updated', handler);
    return () => window.removeEventListener('rating-scale-updated', handler);
  }, [loadRatingScale]);

  return { ratingScale, loading, error };
};